/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRef, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '@/providers/auth';
// RGBARRY-CUSTOMIZATION (DRAFT, W-23789028 consent rework): TrueVault opt-out gating for the engagement adapters.
import { isTrueVaultEnabled } from '@/analytics/truevault-consent-config';
import {
    subscribeTrueVaultConsent,
    getTrueVaultOptedOut,
    getTrueVaultOptedOutServer,
} from '@/analytics/truevault-consent-store';
import type { SessionData } from '@/lib/api/types';
import type { ShopperBasketsV2, ShopperProducts, ShopperSearch, ShopperOrders } from '@/scapi';
// Registers the `purchase` event on the runtime's AnalyticsEvent union (module augmentation)
// so `trackEvent(..., 'purchase', { order })` type-checks. See src/analytics/purchase-event.ts.
import '@/analytics/purchase-event';
import {
    createEvent,
    getEventMediator,
    type EventMediator,
    type AnalyticsEvent,
    type EventSiteInfo,
    type ConsentPreferences,
} from '@salesforce/storefront-next-runtime/events';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { AppConfig } from '@/types/config';
import { ensureAdaptersInitialized } from '@/lib/adapters/engagement/initialize';
import { getAllAdapters, buildConsentPreferences } from '@/lib/adapters';
import { useTrackingConsent } from './use-tracking-consent';
// Registers the new event types on the AnalyticsEvent union so the producers below type-check.
// Type-only imports also activate their module-augmentation side effects.
import type { AnalyticsProductsByItemId, CartAnalyticsSnapshot } from '@/analytics/cart-events';
import type {} from '@/analytics/checkout-events';
import type {} from '@/analytics/wishlist-events';
import type {} from '@/analytics/account-events';

/**
 * Ensures adapters are initialized and returns the event mediator
 *
 * @param appConfig - The application configuration needed to initialize adapters
 * @returns EventMediator instance or undefined if not available
 */
async function getInitializedMediator(appConfig: AppConfig): Promise<EventMediator | undefined> {
    await ensureAdaptersInitialized(appConfig);
    return getEventMediator(getAllAdapters);
}

/**
 * Helper function to track an event with auth validation and mediator initialization
 *
 * @param authPromise - Promise that resolves when auth is available
 * @param appConfig - The application configuration
 * @param consentPreferences - The visitor's granted consent categories (adapters use this for per-adapter filtering)
 * @param siteInfo - Site identification for multi-site support
 * @param eventType - The type of event to track
 * @param eventData - The event data (without user payload, as payload is added automatically)
 * @returns Promise that resolves when tracking is complete or undefined if auth/mediator is unavailable
 */
// ⚠️ Build-time instrumentation check depends on this argument order.
// The `eventInstrumentationValidatorPlugin` in `storefront-next-dev` finds instrumented
// events with a regex that matches the FIRST string literal after the leading positional
// args (`trackEvent(...≥3 non-string args..., 'event_type', ...)`). If you add or remove a
// leading positional arg here (or move `eventType` off its position / pass it as a
// non-literal), update that regex in
// `packages/storefront-next-dev/src/plugins/eventInstrumentationValidator.ts` — otherwise the
// check silently stops finding events and reports false "never instrumented" warnings.
async function trackEvent<TEventType extends AnalyticsEvent['eventType']>(
    authPromise: Promise<SessionData | undefined>,
    appConfig: AppConfig,
    consentPreferences: ConsentPreferences | undefined,
    siteInfo: EventSiteInfo | undefined,
    eventType: TEventType,
    eventData: Omit<Parameters<typeof createEvent<TEventType>>[1], 'payload'>
): Promise<void> {
    // Enabled-by-default (opt-out) posture: new/undecided shoppers ARE tracked — implied consent
    // (defaultTrackingConsent = Accepted, PR #120) on the OOTB path, and "all categories granted until
    // opt-out" on the TrueVault path — so consentPreferences is non-empty for them. This guard skips the
    // auth/mediator work ONLY when consent leaves NO granted categories, i.e. an explicit decline/opt-out.
    // (Per-adapter consent filtering still happens in the mediator for the remaining categories.)
    if (!consentPreferences || consentPreferences.length === 0) {
        return;
    }

    // Wait for auth to be defined before tracking
    const auth = await authPromise;
    if (auth === undefined) {
        // Auth not available - silently fail to not break the app
        return;
    }

    const mediator = await getInitializedMediator(appConfig);
    if (!mediator) {
        return;
    }

    const event = createEvent(eventType, {
        ...eventData,
        payload: {
            userType: auth.userType ?? 'guest',
            encUserId: auth.encUserId ?? undefined,
            usid: auth.usid,
            customerId: auth.customerId,
        },
    } as Parameters<typeof createEvent<TEventType>>[1]);
    return void mediator.track(event, siteInfo, consentPreferences);
}

/**
 * Analytics hook provides tracking functions
 */
export const useAnalytics = () => {
    const auth = useAuth();
    const appConfig = useConfig();
    const { trackingConsent, isTrackingConsentEnabled } = useTrackingConsent();
    const { site, language } = useSite();
    const siteInfo = { siteId: site.id, localeId: language };
    const consentCategories = appConfig.engagement.analytics.trackingConsent?.consentCategories ?? [];
    // RGBARRY-CUSTOMIZATION (DRAFT, W-23789028 consent rework): when TrueVault is the CMP, gate the
    // Salesforce SERVER engagement adapters (einstein/data360/activeData) on the shopper's TrueVault
    // opt-out rather than the SFCC trackingConsent — which is disabled under TrueVault
    // (config.server.ts), so buildConsentPreferences would return all-granted and never block on
    // opt-out. Enabled-by-default: all configured categories are granted until the shopper opts out, at
    // which point only 'necessary' survives, so those adapters stop firing (TrueVault's scanner can't
    // see their server HTTP calls). notOptedOut is read via the truevault-consent store — from the
    // first-party polaris_consent_settings cookie at first render, then Polaris's consentChanged event for
    // in-session changes. NOTE: the ga4DataLayer adapter is intentionally NOT gated by this store — it
    // only pushes to window.dataLayer, and the GTM tags that consume it gate on Google Consent Mode
    // (driven by the TrueVault GTM Custom Template). Under US opt-out, analytics_storage STAYS granted
    // (only the advertising signals deny), so gating GA4 here would over-block first-party analytics
    // Consent Mode permits. See truevault-consent-store.ts "SCOPE" docblock.
    const trueVaultEnabled = isTrueVaultEnabled(appConfig.engagement.consent?.truevault?.scriptUrl);
    const trueVaultOptedOut = useSyncExternalStore(
        subscribeTrueVaultConsent,
        getTrueVaultOptedOut,
        getTrueVaultOptedOutServer
    );
    let consentPreferences: ConsentPreferences | undefined;
    if (trueVaultEnabled) {
        consentPreferences = trueVaultOptedOut
            ? consentCategories.filter((category) => category === 'necessary')
            : [...consentCategories];
    } else {
        // Non-TrueVault path. PR #120 (Einstein) threads defaultTrackingConsent so implied consent
        // (default Accepted) lets undecided shoppers be tracked until they decline. Keep the 4th arg
        // here — omitting it silently reverts undecided shoppers to blocked, undoing #120. The
        // TrueVault-on path above is unaffected: it never calls buildConsentPreferences.
        consentPreferences = buildConsentPreferences(
            trackingConsent,
            consentCategories,
            isTrackingConsentEnabled,
            appConfig.engagement.analytics.trackingConsent?.defaultTrackingConsent
        );
    }

    // Store the promise resolver so we can resolve it when auth becomes available
    const authResolverRef = useRef<((value: SessionData | undefined) => void) | null>(null);

    // Create a promise that resolves when auth is available
    // This promise is updated via useEffect when auth changes
    const authPromiseRef = useRef<Promise<SessionData | undefined>>(
        auth !== undefined
            ? Promise.resolve(auth)
            : new Promise<SessionData | undefined>((resolve) => {
                  authResolverRef.current = resolve;
              })
    );

    // Update the promise when auth changes
    useEffect(() => {
        if (auth !== undefined) {
            // Auth is now available - resolve any pending promises
            if (authResolverRef.current) {
                authResolverRef.current(auth);
                authResolverRef.current = null;
            }
            // Create a new resolved promise for future calls
            authPromiseRef.current = Promise.resolve(auth);
        } else {
            // Auth is undefined - create a new pending promise
            authPromiseRef.current = new Promise<SessionData | undefined>((resolve) => {
                authResolverRef.current = resolve;
            });
        }
    }, [auth]);

    // On the server, return empty functions
    if (typeof window === 'undefined') {
        /* oxlint-disable @typescript-eslint/no-empty-function */
        return {
            trackViewPage: () => {},
            trackViewProduct: () => {},
            trackCartItemAdd: () => {},
            trackViewCart: () => {},
            trackRemoveFromCart: () => {},
            trackCheckoutStart: () => {},
            trackPurchase: () => {},
            trackCheckoutStep: () => {},
            trackAddShippingInfo: () => {},
            trackAddPaymentInfo: () => {},
            trackViewSearch: () => {},
            trackViewCategory: () => {},
            trackClickProductInCategory: () => {},
            trackClickProductInSearch: () => {},
            trackViewRecommender: () => {},
            trackClickProductInRecommender: () => {},
            trackViewSearchSuggestions: () => {},
            trackClickSearchSuggestion: () => {},
            trackWishlistItemAdded: () => {},
            trackWishlistItemRemoved: () => {},
            trackWishlistViewed: () => {},
            trackWishlistItemMerged: () => {},
            trackWishlistMerged: () => {},
            trackNavClick: () => {},
            trackFilter: () => {},
            trackPageError: () => {},
            trackSignUp: () => {},
            trackLogin: () => {},
            trackSubscribe: () => {},
        };
        /* oxlint-enable @typescript-eslint/no-empty-function */
    }

    /**
     * Tracks a page view event
     *
     * Page view events are sent automatically by the PageViewTracker component but this
     * function is provided for manual firing of page views.
     */
    const trackViewPage = async (data: { url: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_page', {
            path: data.url,
        });
    };

    /**
     * Track a product view
     */
    const trackViewProduct = async (data: { product: ShopperProducts.schemas['Product'] }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_product', {
            product: data.product,
        });
    };

    /**
     * Track add to cart
     * RGBARRY-CUSTOMIZATION (Klaviyo parity): accepts optional post-mutation basket enrichment.
     */
    const trackCartItemAdd = async (data: {
        cartItems: ShopperBasketsV2.schemas['ProductItem'][];
        currency?: string;
        basket?: ShopperBasketsV2.schemas['Basket'];
        productsByItemId?: AnalyticsProductsByItemId;
    }) => {
        // Build a fresh, allowlisted object at the event producer boundary. A type assertion alone
        // would leave customerInfo, addresses, and every other Basket property present at runtime
        // when the mediator broadcasts this event to engagement adapters.
        const cartSnapshot: CartAnalyticsSnapshot | undefined = data.basket
            ? {
                  currency: data.basket.currency,
                  orderTotal: data.basket.orderTotal,
                  productTotal: data.basket.productTotal,
                  productSubTotal: data.basket.productSubTotal,
                  productItems: data.basket.productItems,
              }
            : undefined;
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'cart_item_add', {
            cartItems: data.cartItems,
            currency: data.currency,
            cartSnapshot,
            productsByItemId: data.productsByItemId,
        });
    };

    /**
     * Track a cart view (shopper lands on the cart page with a non-empty basket)
     */
    const trackViewCart = async (data: { basket: ShopperBasketsV2.schemas['Basket'] }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_cart', {
            basket: data.basket,
        });
    };

    /**
     * Track removal of a single line item from the cart. The item is captured before the mutation
     * (while still in the basket); `currency` comes from the basket since ProductItem has none.
     */
    const trackRemoveFromCart = async (data: { item: ShopperBasketsV2.schemas['ProductItem']; currency?: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'remove_from_cart', {
            item: data.item,
            currency: data.currency,
        });
    };

    /**
     * Track start of checkout process
     * RGBARRY-CUSTOMIZATION (Klaviyo parity): accepts the checkout loader's optional product map.
     */
    const trackCheckoutStart = async (data: {
        basket: ShopperBasketsV2.schemas['Basket'];
        productsByItemId?: AnalyticsProductsByItemId;
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'checkout_start', {
            basket: data.basket,
            productsByItemId: data.productsByItemId,
        });
    };

    /**
     * Track a completed purchase (order confirmation). Emits the GA4 `purchase` conversion event.
     * Callers are responsible for firing this at most once per order — see the dedup guard in
     * `src/analytics/purchase-event.ts` used by the order-confirmation route.
     */
    const trackPurchase = async (data: { order: ShopperOrders.schemas['Order'] }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'purchase', {
            order: data.order,
        });
    };

    /**
     * Track start of given checkout step
     */
    const trackCheckoutStep = async (data: {
        stepName: string;
        stepNumber: number;
        basket: ShopperBasketsV2.schemas['Basket'];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'checkout_step', {
            stepName: data.stepName,
            stepNumber: data.stepNumber,
            basket: data.basket,
        });
    };

    /**
     * Track the GA4 add_shipping_info funnel milestone (shipping portion of checkout completed).
     * Fired once by the checkout producer when the shopper first reaches/passes the payment step;
     * see src/analytics/checkout-events.ts for why this is producer-driven, not derived from
     * checkout_step.
     */
    const trackAddShippingInfo = async (data: { basket: ShopperBasketsV2.schemas['Basket'] }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'add_shipping_info', {
            basket: data.basket,
        });
    };

    /**
     * Track the GA4 add_payment_info funnel milestone (payment portion of checkout completed).
     * Fired once by the checkout producer when the shopper first reaches/passes the place-order step.
     */
    const trackAddPaymentInfo = async (data: { basket: ShopperBasketsV2.schemas['Basket'] }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'add_payment_info', {
            basket: data.basket,
        });
    };

    /**
     * Track search
     */
    const trackViewSearch = async (data: {
        searchInputText: string;
        searchResults: ShopperSearch.schemas['ProductSearchHit'][];
        sort: string;
        refinements: ShopperSearch.schemas['ProductSearchResult']['selectedRefinements'];
        offset?: number;
        limit?: number;
        total?: number;
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_search', {
            searchInputText: data.searchInputText,
            searchResults: data.searchResults,
            sort: data.sort || '',
            refinements: data.refinements || {},
            offset: data.offset,
            limit: data.limit,
            total: data.total,
        });
    };

    /**
     * Track category view
     */
    const trackViewCategory = async (data: {
        category: ShopperProducts.schemas['Category'];
        searchResults: ShopperSearch.schemas['ProductSearchHit'][];
        sort: string;
        refinements: ShopperSearch.schemas['ProductSearchResult']['selectedRefinements'];
        offset?: number;
        limit?: number;
        total?: number;
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_category', {
            category: data.category,
            searchResults: data.searchResults,
            sort: data.sort || '',
            refinements: data.refinements || {},
            offset: data.offset,
            limit: data.limit,
            total: data.total,
        });
    };

    /**
     * Track click on a product in a category
     */
    const trackClickProductInCategory = async (data: {
        category: ShopperProducts.schemas['Category'];
        product: ShopperSearch.schemas['ProductSearchHit'];
    }) => {
        return trackEvent(
            authPromiseRef.current,
            appConfig,
            consentPreferences,
            siteInfo,
            'click_product_in_category',
            {
                category: data.category,
                product: data.product,
            }
        );
    };

    /**
     * Track click on a product in a search
     */
    const trackClickProductInSearch = async (data: {
        searchInputText: string;
        product: ShopperSearch.schemas['ProductSearchHit'];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'click_product_in_search', {
            searchInputText: data.searchInputText,
            product: data.product,
        });
    };

    /**
     * Track a recommender impression (carousel scrolled into view with resolved recs)
     */
    const trackViewRecommender = async (data: {
        recommenderId: string;
        recommenderName: string;
        products: ShopperSearch.schemas['ProductSearchHit'][];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_recommender', {
            recommenderId: data.recommenderId,
            recommenderName: data.recommenderName,
            products: data.products,
        });
    };

    /**
     * Track click on a product in a recommender carousel
     */
    const trackClickProductInRecommender = async (data: {
        recommenderId: string;
        recommenderName: string;
        product: ShopperSearch.schemas['ProductSearchHit'];
    }) => {
        return trackEvent(
            authPromiseRef.current,
            appConfig,
            consentPreferences,
            siteInfo,
            'click_product_in_recommender',
            {
                recommenderId: data.recommenderId,
                recommenderName: data.recommenderName,
                product: data.product,
            }
        );
    };

    /**
     * Track view of search suggestions
     */
    const trackViewSearchSuggestions = async (data: { searchInputText: string; suggestions: Array<string> }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'view_search_suggestion', {
            searchInputText: data.searchInputText,
            suggestions: data.suggestions,
        });
    };

    /**
     * Track click on a search suggestion
     */
    const trackClickSearchSuggestion = async (data: { searchInputText: string; suggestion: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'click_search_suggestion', {
            searchInputText: data.searchInputText,
            suggestion: data.suggestion,
        });
    };

    /**
     * Track wishlist item added
     */
    const trackWishlistItemAdded = async (data: {
        surface: 'pdp' | 'plp' | 'cart' | 'wishlist-page';
        productId: string;
        productName?: string;
        price?: number;
        currency?: string;
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'wishlist_item_added', {
            surface: data.surface,
            productId: data.productId,
            productName: data.productName,
            price: data.price,
            currency: data.currency,
        });
    };

    /**
     * Track wishlist item removed
     */
    const trackWishlistItemRemoved = async (data: {
        surface: 'pdp' | 'plp' | 'cart' | 'wishlist-page';
        productId: string;
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'wishlist_item_removed', {
            surface: data.surface,
            productId: data.productId,
        });
    };

    /**
     * Track wishlist page view
     */
    const trackWishlistViewed = async () => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'wishlist_viewed', {});
    };

    /**
     * Track individual product merged from guest to registered wishlist
     */
    const trackWishlistItemMerged = async (data: { productId: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'wishlist_item_merged', {
            productId: data.productId,
        });
    };

    /**
     * Track wishlist merge operation summary on login
     */
    const trackWishlistMerged = async (data: {
        merged: number;
        skipped: number;
        failed: number;
        mergedProductIds: string[];
        skippedProductIds: string[];
        failedProductIds: string[];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'wishlist_merged', data);
    };

    /**
     * Track a navigation click. Carries up to three hierarchy labels (top-level → sub → leaf);
     * the GA4 adapter maps them to the container's `nav_level_1..3` dataLayer fields.
     */
    const trackNavClick = async (data: { navLevel1: string; navLevel2?: string; navLevel3?: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'nav_click', {
            navLevel1: data.navLevel1,
            navLevel2: data.navLevel2,
            navLevel3: data.navLevel3,
        });
    };

    /**
     * Track application of a PLP/search refinement. `name` is the refinement attribute (label when
     * available, else the attribute id); `value` is the selected refinement value.
     */
    const trackFilter = async (data: { name: string; value: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'filter', {
            name: data.name,
            value: data.value,
        });
    };

    /**
     * Track a client-side page error / 404 rendered by the root error boundary. `message` should
     * be a concise, PII-free description (HTTP status + status text/message).
     */
    const trackPageError = async (data: { message: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'page_error', {
            message: data.message,
        });
    };

    /**
     * RGBARRY-CUSTOMIZATION (W-23955510): track a successful account registration. Emits the GA4
     * `sign_up` conversion event (restores the SFRA FB "Complete Registration" tag [90]). `method`
     * records how the account was created (e.g. 'email', 'passwordless', 'social:Google').
     */
    const trackSignUp = async (data: { method?: string } = {}) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'sign_up', {
            method: data.method,
        });
    };

    /**
     * RGBARRY-CUSTOMIZATION (W-23955510): track a successful login. Emits the GA4 `login` event.
     * `method` records the mechanism (e.g. 'password', 'passkey', 'passwordless', 'social:Apple').
     */
    const trackLogin = async (data: { method?: string } = {}) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'login', {
            method: data.method,
        });
    };

    /**
     * RGBARRY-CUSTOMIZATION (W-23955510): track a successful newsletter/SMS subscription. The GA4
     * adapter maps this to `generate_lead` (restores the SFRA FB "Email Lead" [89] and Reddit
     * "Email Signup" [100] tags). `method` is the channel (email|sms); `source` is where the shopper
     * subscribed (e.g. 'footer', 'checkout').
     */
    const trackSubscribe = async (data: { method: 'email' | 'sms'; source?: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, consentPreferences, siteInfo, 'subscribe', {
            method: data.method,
            source: data.source,
        });
    };

    return {
        trackViewPage,
        trackViewProduct,
        trackCartItemAdd,
        trackViewCart,
        trackRemoveFromCart,
        trackCheckoutStart,
        trackPurchase,
        trackCheckoutStep,
        trackAddShippingInfo,
        trackAddPaymentInfo,
        trackViewSearch,
        trackViewCategory,
        trackClickProductInCategory,
        trackClickProductInSearch,
        trackViewRecommender,
        trackClickProductInRecommender,
        trackViewSearchSuggestions,
        trackClickSearchSuggestion,
        trackWishlistItemAdded,
        trackWishlistItemRemoved,
        trackWishlistViewed,
        trackWishlistItemMerged,
        trackWishlistMerged,
        trackNavClick,
        trackFilter,
        trackPageError,
        trackSignUp,
        trackLogin,
        trackSubscribe,
    };
};
